import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCheck, Loader2, Pencil, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { shortDate } from "@/lib/invoice";
import SkipList from "@/components/invoices/SkipList";
import SkipOverride from "@/components/invoices/SkipOverride";

const STATUS = { paid: "text-emerald-400 bg-emerald-400/10", unpaid: "text-amber-400 bg-amber-400/10", draft: "text-muted-foreground bg-secondary" };

export default function RouteResult({ result, onFinalized, onOverride }) {
  const [busy, setBusy] = useState(false);
  const drafts = result.invoices.filter((x) => x.status === "draft");

  const finalize = async () => {
    if (!window.confirm(`Turn ${drafts.length} draft invoice${drafts.length === 1 ? "" : "s"} into unpaid invoices?`)) return;
    setBusy(true);
    try {
      const ids = drafts.map((x) => x.id);
      const { data } = await api.post("/invoices/finalize", { ids });
      onFinalized(ids);
      toast.success(`${data.updated} invoice${data.updated === 1 ? "" : "s"} finalized as unpaid`);
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };

  return (
    <div className="border rounded-lg bg-card p-4 space-y-3" data-testid="route-result">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm">
          <span className="font-bold text-emerald-400" data-testid="route-created-count">{result.invoices.length}</span> route invoices created
          {result.skipped.length > 0 && <>, <span className="font-bold text-amber-400" data-testid="route-skipped-count">{result.skipped.length}</span> skipped</>}
          <span className="text-muted-foreground"> · changes you make here stay on the invoices, the main database is not changed</span>
          {result.routeDb && <> · <Link to={`/db/${result.routeDb.id}/${encodeURIComponent(result.routeDb.table)}`} className="text-primary hover:underline" data-testid="route-db-link">Open sub database "{result.routeDb.name}"</Link></>}
        </p>
        <div className="flex gap-2">
          {drafts.length > 0 && (
            <Button variant="outline" onClick={finalize} disabled={busy} data-testid="route-finalize-drafts">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />} Finalize {drafts.length} drafts
            </Button>
          )}
          {result.invoices.length > 0 && (
            <Button asChild><a href={`/invoices/batch/${result.batch_id}/print`} target="_blank" rel="noreferrer" data-testid="route-print-all"><Printer size={14} /> Print all in route order</a></Button>
          )}
        </div>
      </div>
      {result.skipped.length > 0 && <SkipList skipped={result.skipped} />}
      {result.skipped.length > 0 && onOverride && <SkipOverride result={result} invoices={result.sent || []} onDone={onOverride} />}
      <ol className="divide-y border rounded-md text-sm">
        {result.invoices.map((inv, i) => (
          <li key={inv.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`route-result-${i}`}>
            <span><span className="font-mono text-xs text-muted-foreground">{i + 1}.</span> <span className="font-mono">{inv.number}</span> · {inv.customer?.name} · {shortDate(inv.service_date)}
              <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase font-mono ${STATUS[inv.status] || ""}`} data-testid={`route-result-status-${i}`}>{inv.status}</span></span>
            <Button size="sm" variant="outline" asChild><Link to={`/invoices/${inv.id}`} data-testid={`route-result-edit-${i}`}><Pencil size={14} /> Edit</Link></Button>
          </li>
        ))}
      </ol>
    </div>
  );
}
